import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Link, router, useLocalSearchParams } from 'expo-router';
import { Eye, EyeOff } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { z } from 'zod';

import { Button } from '@/src/components/Button';
import { Input } from '@/src/components/Input';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import {
  emailVerificationFlowFromActionParams,
  getAuthActionSuccessAction,
  type AuthActionMode,
} from '@/src/features/auth/auth-action-routing';
import { syncCurrentAuthEmail } from '@/src/features/auth/auth-service';
import { auth, db } from '@/src/lib/firebase';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type ActionState = 'checking' | 'ready' | 'applying' | 'success' | 'error';

type PasswordResetValues = {
  password: string;
  confirmPassword: string;
};

const passwordResetSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters.'),
    confirmPassword: z.string().min(1, 'Confirm your new password.'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match.',
  });

function firstParam(value: string | string[] | undefined) {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
}

function isSupportedMode(mode: string | undefined): mode is AuthActionMode {
  return (
    mode === 'recoverEmail' ||
    mode === 'resetPassword' ||
    mode === 'revertSecondFactorAddition' ||
    mode === 'verifyAndChangeEmail' ||
    mode === 'verifyEmail'
  );
}

function getActionCopy(mode: AuthActionMode | undefined) {
  switch (mode) {
    case 'recoverEmail':
      return {
        title: 'Email restored',
        checking: 'Restoring your previous sign-in email...',
        success:
          'Your sign-in email has been restored. If this was not you, reset your password now.',
      };
    case 'resetPassword':
      return {
        title: 'Change password',
        checking: 'Checking your password reset link...',
        success: 'Your password has been changed. You can sign in now.',
      };
    case 'revertSecondFactorAddition':
      return {
        title: 'Security updated',
        checking: 'Removing the recently added second factor...',
        success:
          'The second factor was removed. Review your account security after signing in.',
      };
    case 'verifyAndChangeEmail':
      return {
        title: 'Email changed',
        checking: 'Confirming your new sign-in email...',
        success: 'Your new sign-in email has been confirmed.',
      };
    case 'verifyEmail':
      return {
        title: 'Email verified',
        checking: 'Verifying your email address...',
        success: 'Your email address has been verified.',
      };
    default:
      return {
        title: 'Account action',
        checking: 'Checking this account action...',
        success: 'This account action is complete.',
      };
  }
}

function getFriendlyActionError(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof error.code === 'string'
  ) {
    if (
      error.code === 'auth/expired-action-code' ||
      error.code === 'auth/invalid-action-code'
    ) {
      return 'This link is expired or has already been used. Request a new email and try again.';
    }
  }

  return 'We could not complete this account action. Request a new email and try again.';
}

async function currentUserHasProfile() {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    return false;
  }

  try {
    const userSnapshot = await getDoc(doc(db, 'users', uid));
    return userSnapshot.exists();
  } catch (error) {
    console.warn(
      'Could not check profile before syncing verified email.',
      error,
    );
    return false;
  }
}

export default function AuthActionScreen() {
  const params = useLocalSearchParams();
  const { showError, showSuccess } = useToast();
  const didRunActionRef = useRef(false);
  const modeParam = firstParam(params.mode);
  const oobCode = firstParam(params.oobCode);
  const flowParam = firstParam(params.flow);
  const continueUrlParam = firstParam(params.continueUrl);
  const mode = isSupportedMode(modeParam) ? modeParam : undefined;
  const emailVerificationFlow = useMemo(
    () =>
      emailVerificationFlowFromActionParams({
        continueUrl: continueUrlParam,
        flow: flowParam,
      }),
    [continueUrlParam, flowParam],
  );
  const copy = useMemo(() => getActionCopy(mode), [mode]);
  const [successAction, setSuccessAction] = useState(() =>
    getAuthActionSuccessAction({
      emailVerificationFlow,
      hasProfile: false,
      mode,
    }),
  );
  const [state, setState] = useState<ActionState>('checking');
  const [email, setEmail] = useState<string | null>(null);
  const [message, setMessage] = useState(copy.checking);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm<PasswordResetValues>({
    defaultValues: {
      confirmPassword: '',
      password: '',
    },
  });

  useEffect(() => {
    if (didRunActionRef.current) {
      return;
    }

    didRunActionRef.current = true;

    async function runAction() {
      if (!mode || !oobCode) {
        setState('error');
        setMessage('This account action link is missing required information.');
        return;
      }

      try {
        if (mode === 'resetPassword') {
          const resetEmail = await verifyPasswordResetCode(auth, oobCode);
          setEmail(resetEmail);
          setState('ready');
          setMessage('Enter a new password for your Frienzzi account.');
          return;
        }

        setState('applying');
        await checkActionCode(auth, oobCode);
        await applyActionCode(auth, oobCode);
        if (
          auth.currentUser &&
          (mode === 'verifyEmail' || mode === 'verifyAndChangeEmail')
        ) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);

          const hasProfile = await currentUserHasProfile();

          if (mode === 'verifyEmail' || mode === 'verifyAndChangeEmail') {
            setSuccessAction(
              getAuthActionSuccessAction({
                emailVerificationFlow,
                hasProfile,
                mode,
              }),
            );
          }

          if (mode === 'verifyAndChangeEmail' && hasProfile) {
            try {
              await syncCurrentAuthEmail();
            } catch (syncError) {
              console.warn(
                'Verified auth email changed before profile email sync.',
                syncError,
              );
            }
          }
        }
        setState('success');
        setMessage(copy.success);
        showSuccess(copy.success, copy.title);
      } catch (error) {
        const friendlyError = getFriendlyActionError(error);
        setState('error');
        setMessage(friendlyError);
        showError(error, friendlyError);
      }
    }

    void runAction();
  }, [
    copy.success,
    copy.title,
    emailVerificationFlow,
    mode,
    oobCode,
    showError,
    showSuccess,
  ]);

  const onSubmit = handleSubmit(async (values) => {
    if (!oobCode) {
      setState('error');
      setMessage('This password reset link is missing required information.');
      return;
    }

    const parsed = passwordResetSchema.safeParse(values);

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];

        if (field === 'password' || field === 'confirmPassword') {
          setError(field, { message: issue.message });
        }
      });
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, parsed.data.password);
      setState('success');
      setMessage(copy.success);
      showSuccess(copy.success, 'Password changed');
    } catch (error) {
      const friendlyError = getFriendlyActionError(error);
      setState('error');
      setMessage(friendlyError);
      showError(error, friendlyError);
    }
  });

  const showPasswordForm = mode === 'resetPassword' && state === 'ready';
  const isBusy = state === 'checking' || state === 'applying';

  return (
    <Screen
      showBrand
      title={copy.title}
      subtitle="Secure Frienzzi account update"
    >
      <View style={styles.card}>
        {isBusy ? (
          <Text style={styles.statusText}>{copy.checking}</Text>
        ) : (
          <Text
            style={[
              styles.statusText,
              state === 'error' ? styles.errorText : null,
            ]}
          >
            {message}
          </Text>
        )}

        {email ? <Text style={styles.emailText}>{email}</Text> : null}

        {showPasswordForm ? (
          <View style={styles.form}>
            <Controller
              control={control}
              name="password"
              render={({ field: { onBlur, onChange, value } }) => (
                <Input
                  label="New password"
                  autoComplete="new-password"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  passwordRules="minlength: 8;"
                  secureTextEntry={!passwordVisible}
                  textContentType="newPassword"
                  value={value}
                  error={errors.password?.message}
                  rightAccessory={
                    <Pressable
                      accessibilityLabel={
                        passwordVisible ? 'Hide password' : 'Show password'
                      }
                      accessibilityRole="button"
                      onPress={() => setPasswordVisible((visible) => !visible)}
                      style={styles.passwordToggle}
                    >
                      {passwordVisible ? (
                        <EyeOff
                          color={colors.secondary}
                          size={20}
                          strokeWidth={2.4}
                        />
                      ) : (
                        <Eye
                          color={colors.secondary}
                          size={20}
                          strokeWidth={2.4}
                        />
                      )}
                    </Pressable>
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field: { onBlur, onChange, value } }) => (
                <Input
                  label="Confirm new password"
                  autoComplete="new-password"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  passwordRules="minlength: 8;"
                  secureTextEntry={!confirmPasswordVisible}
                  textContentType="newPassword"
                  value={value}
                  error={errors.confirmPassword?.message}
                  rightAccessory={
                    <Pressable
                      accessibilityLabel={
                        confirmPasswordVisible
                          ? 'Hide confirm password'
                          : 'Show confirm password'
                      }
                      accessibilityRole="button"
                      onPress={() =>
                        setConfirmPasswordVisible((visible) => !visible)
                      }
                      style={styles.passwordToggle}
                    >
                      {confirmPasswordVisible ? (
                        <EyeOff
                          color={colors.secondary}
                          size={20}
                          strokeWidth={2.4}
                        />
                      ) : (
                        <Eye
                          color={colors.secondary}
                          size={20}
                          strokeWidth={2.4}
                        />
                      )}
                    </Pressable>
                  }
                />
              )}
            />
            <Button
              title="Change password"
              loading={isSubmitting}
              onPress={onSubmit}
            />
          </View>
        ) : null}

        {state === 'success' ? (
          <Button
            title={successAction.title}
            onPress={() => router.replace(successAction.path)}
          />
        ) : null}

        {state === 'error' ? (
          <View style={styles.links}>
            <Button
              title="Go to sign in"
              variant="secondary"
              onPress={() => router.replace('/sign-in')}
            />
            <Text style={styles.helpText}>
              Need a new link? Go to{' '}
              <Link href="/sign-in" style={styles.link}>
                sign in
              </Link>{' '}
              and request another password reset email.
            </Text>
          </View>
        ) : null}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.lg,
    padding: spacing.xl,
  },
  emailText: {
    color: colors.secondary,
    fontSize: 15,
    fontWeight: '700',
  },
  errorText: {
    color: colors.danger,
  },
  form: {
    gap: spacing.lg,
  },
  helpText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  link: {
    color: colors.primary,
    fontWeight: '700',
  },
  links: {
    gap: spacing.md,
  },
  passwordToggle: {
    alignItems: 'center',
    borderRadius: 999,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  statusText: {
    color: colors.text,
    fontSize: 17,
    lineHeight: 24,
  },
});
